/**
 * useAppointmentBooking — All state and handlers for the AddAppointmentDialog.
 * Extracted for maintainability. The dialog component is now pure rendering.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { ghlApi } from '../lib/ghlApi';
import { crmNavigate } from '../lib/crmNavigate';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const JUVONNO_FIELD_KEY = 'contact.juvonno_customer_id';
const EMPTY_FORM = { firstName: '', lastName: '', email: '', phone: '', gender: '', dateOfBirth: '', notes: '' };
const fullName = (f) => `${(f?.firstName || '').trim()} ${(f?.lastName || '').trim()}`.trim();

function generateTimeSlots() {
  const slots = [];
  for (let h = 5; h <= 22; h++) {
    for (let m = 0; m < 60; m += 5) {
      const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
      const period = h >= 12 ? 'PM' : 'AM';
      slots.push(`${hour12}:${m.toString().padStart(2, '0')} ${period}`);
    }
  }
  return slots;
}

export function useAppointmentBooking({
  open, onOpenChange, calendars, onSubmit, prefill, locationId, appointmentTypes,
  initialContact, onBooked,
}) {
  const { user, currentLocation: authLoc } = useAuth();
  const currentLocation = authLoc || JSON.parse(localStorage.getItem('current_location') || 'null');

  const [tab, setTab] = useState('search');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const searchPending = useRef(false);
  const searchVersionRef = useRef(0);
  const ghlPoolRef = useRef({ query: '', contacts: [], complete: false });
  const [selectedContact, setSelectedContact] = useState(null);
  // Tracks how the contact entered the booking — 'search' (existing) or 'created' (brand new
  // during this session). Used by the audit log so existing-vs-new is reported correctly
  // even after a freshly-created contact gets pushed into selectedContact.
  const [contactSource, setContactSource] = useState(null);

  const [contactForm, setContactForm] = useState({ ...EMPTY_FORM });
  const [contactDirty, setContactDirty] = useState(false);
  const [updatingContact, setUpdatingContact] = useState(false);
  const [juvonnoId, setJuvonnoId] = useState('');

  // Juvonno verify state
  const [verifyStatus, setVerifyStatus] = useState(null); // null | 'loading' | 'verified' | 'mismatch'
  const [verifyDetails, setVerifyDetails] = useState(null);
  const [verifiedAt, setVerifiedAt] = useState(null);

  // Appointment details
  const [calendarId, setCalendarId] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [appointmentType, setAppointmentType] = useState('');
  const [duration, setDuration] = useState('30');
  const [submitting, setSubmitting] = useState(false);
  const [notes, setNotes] = useState('');
  const [genderFieldId, setGenderFieldId] = useState(null);
  const [customFieldDefs, setCustomFieldDefs] = useState([]);
  const [showForceSave, setShowForceSave] = useState(false);
  const [lastError, setLastError] = useState(null);
  const [errorType, setErrorType] = useState('unknown');

  // Save new contact to CRM
  const [savingNewContact, setSavingNewContact] = useState(false);

  // Duplicate contact detection
  const [dupMatches, setDupMatches] = useState([]);
  const [dupSearching, setDupSearching] = useState(false);

  // Unsaved-changes guard — confirms before discarding edits to a contact.
  // We trigger it from the X close-button on the dialog, the deselect-contact
  // button, the back-to-search button, and the Dialog's onOpenChange callback.
  const attemptCloseContactForm = useCallback((onConfirm) => {
    if (!contactDirty) {
      onConfirm();
      return;
    }
    const proceed = window.confirm('You have unsaved changes to this contact. Discard them?');
    if (!proceed) return;
    setContactDirty(false);
    onConfirm();
  }, [contactDirty]);

  const handleDialogOpenChange = useCallback((next) => {
    if (next) {
      onOpenChange(true);
      return;
    }
    // Closing — guard if dirty
    if (contactDirty) {
      const proceed = window.confirm('You have unsaved changes to this contact. Discard them and close?');
      if (!proceed) return;
      setContactDirty(false);
    }
    onOpenChange(false);
  }, [contactDirty, onOpenChange]);

  useEffect(() => {
    if (!open) return;
    setTab('search');
    setSearchQuery('');
    setSearchResults([]);
    setSelectedContact(null);
    setContactSource(null);
    setContactForm({ ...EMPTY_FORM });
    setContactDirty(false);
    setJuvonnoId('');
    setVerifyStatus(null);
    setVerifyDetails(null);
    setVerifiedAt(null);
    setSubmitting(false);
    setNotes('');
    setShowForceSave(false);
    setLastError(null);
    setErrorType('unknown');
    setDupMatches([]);
    setDupSearching(false);
    setSavingNewContact(false);
    ghlPoolRef.current = { query: '', contacts: [], complete: false };
    if (prefill) {
      setCalendarId(prefill.calendarId || '');
      setDate(prefill.date || '');
      setTime(prefill.time || '');
      setDuration(prefill.duration || '30');
      if (prefill.forceBook) {
        setShowForceSave(true);
      }
    } else {
      setCalendarId(calendars[0]?.id || '');
      setDate('');
      setTime('');
      setDuration('30');
    }
    setAppointmentType(appointmentTypes[0]?.name || '');

    // Load gender custom field ID FIRST, then auto-select contact
    const initContact = async () => {
      let resolvedGenderFieldId = genderFieldId;
      let resolvedJuvFieldId = null;
      let loadedFieldDefs = [];
      if (locationId) {
        try {
          const resp = await ghlApi.getCustomFields(locationId);
          loadedFieldDefs = resp.data?.customFields || [];
          setCustomFieldDefs(loadedFieldDefs);
          for (const f of loadedFieldDefs) {
            const fname = (f.name || '').toLowerCase().trim();
            const fkey = (f.fieldKey || '').toLowerCase().trim();
            if (fname === 'gender' || fkey === 'gender' || fkey === 'contact.gender') {
              resolvedGenderFieldId = f.id;
              setGenderFieldId(f.id);
            }
            if (fkey === JUVONNO_FIELD_KEY || fname.includes('juvonno')) {
              resolvedJuvFieldId = f.id;
            }
          }
        } catch {}
      }
      if (initialContact) {
        handleSelectContact(initialContact, resolvedGenderFieldId, resolvedJuvFieldId);
      } else if (prefill?.contactId && locationId) {
        await new Promise(r => setTimeout(r, 300));
        try {
          const resp = await ghlApi.getContact(prefill.contactId, locationId);
          const contact = resp.data?.contact || resp.data;
          if (contact) {
            handleSelectContact(contact, resolvedGenderFieldId, resolvedJuvFieldId);
          }
        } catch (e) {
          console.error('Auto-select contact failed:', e);
        }
      }
    };
    initContact();
  }, [open, prefill, calendars, appointmentTypes, initialContact]);

  // Restore persisted verification state when contact/juvonnoId changes
  useEffect(() => {
    if (!juvonnoId || !selectedContact?.id || !locationId) return;
    // Check DB first
    ghlApi.isJuvonnoVerified(locationId, selectedContact.id).then(r => {
      if (r.data?.verified) {
        setVerifyStatus('verified');
        setVerifiedAt(r.data.verified_at);
      } else {
        // Fallback to localStorage
        try {
          const stored = JSON.parse(localStorage.getItem('juvonno_verifications') || '{}');
          if (stored[juvonnoId]) { setVerifyStatus('verified'); setVerifiedAt(stored[juvonnoId]); }
        } catch {}
      }
    }).catch(() => {});
  }, [juvonnoId, selectedContact?.id, locationId]);

  // Local name filter — startsWith on word boundaries
  const filterByName = useCallback((contacts, query) => {
    const trimmed = query.trim();
    const parts = trimmed.split(/\s+/).filter(Boolean);
    if (!parts.length) return contacts;

    const isEmailSearch = trimmed.includes('@');
    const isPhoneSearch = /^\+?[\d\s()\-]{7,}$/.test(trimmed);
    if (isEmailSearch || isPhoneSearch) return contacts;

    if (parts.length === 1) {
      const lp = parts[0].toLowerCase();
      return contacts.filter(c => {
        const nameWords = `${c.firstName || ''} ${c.lastName || ''}`.toLowerCase().trim().split(/\s+/).filter(Boolean);
        return nameWords.some(w => w.startsWith(lp));
      });
    }

    const lowerParts = parts.map(p => p.toLowerCase());
    return contacts.filter(c => {
      const nameWords = `${c.firstName || ''} ${c.lastName || ''}`.toLowerCase().trim().split(/\s+/).filter(Boolean);
      const used = new Set();
      return lowerParts.every(p => {
        const idx = nameWords.findIndex((w, i) => !used.has(i) && w.startsWith(p));
        if (idx >= 0) { used.add(idx); return true; }
        return false;
      });
    });
  }, []);

  // GHL search — fetches ALL matching contacts via backend auto-pagination
  const doSearch = useCallback(async (q) => {
    searchPending.current = false;
    const version = ++searchVersionRef.current;

    if (!q || q.length < 2 || !locationId) {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    try {
      const trimmed = q.trim();
      const digits = trimmed.replace(/\D/g, '');
      const isPhoneLike = digits.length >= 6;

      // ===== PHONE-FIRST PATH =====
      // When input looks like a phone number, skip name search — go straight to phone matching
      if (isPhoneLike) {
        // Build all reasonable phone variants to search
        const variants = new Set();
        variants.add(digits);
        // Strip leading 1 (country code)
        if (digits.startsWith('1') && digits.length === 11) variants.add(digits.slice(1));
        // Add leading 1
        if (!digits.startsWith('1') && digits.length === 10) variants.add('1' + digits);
        if (!digits.startsWith('1') && digits.length <= 10) variants.add('1' + digits);
        // Also try with + prefix for GHL
        variants.add('+1' + (digits.startsWith('1') ? digits.slice(1) : digits));

        let allPhoneContacts = [];
        for (const pv of variants) {
          if (version !== searchVersionRef.current) return;
          const pr = await ghlApi.searchAllContacts(locationId, pv).catch(() => ({ data: { contacts: [] } }));
          if (version !== searchVersionRef.current) return;
          const found = pr.data?.contacts || [];
          // Deduplicate by contact ID
          for (const c of found) {
            if (!allPhoneContacts.some(x => x.id === c.id)) allPhoneContacts.push(c);
          }
        }

        // Filter to contacts whose phone actually contains the searched digits
        const exactMatches = allPhoneContacts.filter(c => {
          const cDigits = (c.phone || '').replace(/\D/g, '');
          // Match if contact's phone contains the search digits or vice versa
          return cDigits && (cDigits.includes(digits) || digits.includes(cDigits) ||
            // Also match without country code
            (cDigits.startsWith('1') && cDigits.slice(1).includes(digits)) ||
            (digits.startsWith('1') && cDigits.includes(digits.slice(1))));
        });

        if (version !== searchVersionRef.current) return;
        // Show exact phone matches first, then other results
        if (exactMatches.length > 0) {
          setSearchResults(exactMatches);
        } else {
          // Show all found contacts as partial matches
          setSearchResults(allPhoneContacts.slice(0, 10));
        }
        setSearching(false);
        return;
      }

      // ===== NAME SEARCH PATH =====
      const resp = await ghlApi.searchAllContacts(locationId, trimmed).catch(() => ({ data: { contacts: [] } }));
      if (version !== searchVersionRef.current) return;

      const allContacts = resp.data?.contacts || [];
      const words = trimmed.split(/\s+/).filter(Boolean);
      const poolKey = words.length > 1 ? words[0].toLowerCase() : trimmed.toLowerCase();
      ghlPoolRef.current = { query: poolKey, contacts: allContacts, complete: true };

      const results = filterByName(allContacts, q);

      if (version !== searchVersionRef.current) return;
      setSearchResults(results);
    } catch {
      if (version === searchVersionRef.current) setSearchResults([]);
    } finally {
      if (version === searchVersionRef.current) setSearching(false);
    }
  }, [locationId, filterByName]);

  // Smart debounce: instant local filter when narrowing, 250ms GHL call otherwise
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults([]);
      setSearching(false);
      return;
    }

    const trimmed = searchQuery.trim();
    const digits = trimmed.replace(/\D/g, '');
    const isPhoneLike = digits.length >= 6;

    // NEVER use local pool for phone searches — always go to doSearch for proper phone matching
    if (!isPhoneLike) {
      const pool = ghlPoolRef.current;
      const canFilterLocally = pool.query && pool.contacts.length > 0 &&
        pool.complete &&
        trimmed.toLowerCase().startsWith(pool.query.toLowerCase());

      if (canFilterLocally) {
        const localResults = filterByName(pool.contacts, searchQuery);
        if (localResults.length > 0) {
          setSearchResults(localResults);
          setSearching(false);
          return;
        }
        setSearchResults([]);
      }
    }

    // Debounce + GHL API call
    setSearching(true);
    const t = setTimeout(() => doSearch(searchQuery), 150);
    return () => clearTimeout(t);
  }, [searchQuery, doSearch, filterByName]);

  // Duplicate contact detection — runs when email or phone changes during new contact creation
  useEffect(() => {
    if (selectedContact || tab !== 'create' || !locationId) {
      setDupMatches([]);
      return;
    }
    const email = (contactForm.email || '').trim();
    const phone = (contactForm.phone || '').trim();
    // Only search when email has @ or phone has 5+ digits
    const hasEmail = email.includes('@') && email.includes('.') && email.length >= 8;
    const hasPhone = phone.replace(/\D/g, '').length >= 10;
    if (!hasEmail && !hasPhone) { setDupMatches([]); return; }
    const q = hasEmail ? email : phone;
    const t = setTimeout(async () => {
      setDupSearching(true);
      try {
        const resp = await ghlApi.getContacts(locationId, { query: q, limit: 3 });
        setDupMatches(resp.data?.contacts || []);
      } catch { setDupMatches([]); }
      finally { setDupSearching(false); }
    }, 1500);
    return () => clearTimeout(t);
  }, [contactForm.email, contactForm.phone, selectedContact, tab, locationId]);

  // Select contact
  const handleSelectContact = async (contact, gfIdOverride, juvFieldIdOverride) => {
    setSelectedContact(contact);
    setContactSource('search');
    setSearchQuery('');
    setSearchResults([]);
    setVerifyStatus(null);
    setVerifyDetails(null);

    const cName = contact.contactName || `${contact.firstName || ''} ${contact.lastName || ''}`.trim();
    // Detect if the "name" is actually a phone number (unnamed contacts)
    const isPhoneNumber = /^[\d\s\(\)\-\+]+$/.test(cName.replace(/\s/g, ''));
    const titleCase = (str) => str.split(/\s+/).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    // If name is a phone number, leave first/last blank — phone goes in the phone field
    let firstNameVal = '';
    let lastNameVal = '';
    if (!isPhoneNumber) {
      firstNameVal = titleCase(contact.firstName || cName.split(' ')[0] || '').trim();
      lastNameVal = titleCase(contact.lastName || cName.split(' ').slice(1).join(' ') || '').trim();
    }
    const cf = contact.customFields || [];
    const effectiveGfId = gfIdOverride || genderFieldId;

    // Resolve gender: top-level → custom field by ID → custom field by key
    let gender = contact.gender || '';
    if (!gender && effectiveGfId) {
      const genderCf = cf.find(f => f.id === effectiveGfId);
      gender = genderCf?.value || '';
    }
    if (!gender) {
      for (const f of cf) {
        const fkey = (f.fieldKey || f.key || '').toLowerCase();
        if (fkey === 'gender' || fkey === 'contact.gender') {
          gender = f.value || '';
          break;
        }
      }
    }
    setContactForm({
      firstName: firstNameVal,
      lastName: lastNameVal,
      email: contact.email || '',
      phone: contact.phone || '',
      gender: gender.toLowerCase(),
      dateOfBirth: contact.dateOfBirth || '',
      notes: '',
    });
    originalContactRef.current = {
      firstName: firstNameVal,
      lastName: lastNameVal,
      email: contact.email || '',
      phone: contact.phone || '',
      gender: gender.toLowerCase(),
      dateOfBirth: contact.dateOfBirth || '',
    };
    setContactDirty(false);

    // Extract Juvonno Customer ID from custom fields
    const effectiveJuvId = juvFieldIdOverride || (customFieldDefs.find(f => f.fieldKey === JUVONNO_FIELD_KEY) || {}).id;
    let juvValue = '';
    if (effectiveJuvId) {
      const jField = cf.find(f => f.id === effectiveJuvId);
      juvValue = jField?.value || '';
    }
    setJuvonnoId(juvValue);

    // Always fetch full contact detail to ensure Juvonno ID, gender, DOB are loaded
    if (contact.id) {
      try {
        const detResp = await ghlApi.getContact(contact.id, locationId);
        const det = detResp.data?.contact || detResp.data || {};
        const detCf = det.customFields || [];
        
        // Find Juvonno ID by field ID
        if (!juvValue && effectiveJuvId) {
          const jField = detCf.find(f => f.id === effectiveJuvId);
          if (jField?.value) { juvValue = jField.value; setJuvonnoId(juvValue); }
        }
        if (det.dateOfBirth && !contact.dateOfBirth) {
          setContactForm(prev => ({ ...prev, dateOfBirth: det.dateOfBirth }));
          originalContactRef.current = { ...originalContactRef.current, dateOfBirth: det.dateOfBirth };
        }
        // Pull gender from custom field if not set
        let detGender = det.gender || '';
        if (!detGender && effectiveGfId) {
          const gField = detCf.find(f => f.id === effectiveGfId);
          detGender = gField?.value || '';
        }
        if (!detGender) {
          for (const f of detCf) {
            const fkey = (f.fieldKey || f.key || '').toLowerCase();
            if (fkey === 'gender' || fkey === 'contact.gender') {
              detGender = f.value || '';
              break;
            }
          }
        }
        if (detGender) {
          const g = detGender.toLowerCase();
          setContactForm(prev => ({ ...prev, gender: prev.gender || g }));
          originalContactRef.current = { ...originalContactRef.current, gender: originalContactRef.current?.gender || g };
        }
        // Also fill email if missing
        if (det.email && !contact.email) {
          setContactForm(prev => ({ ...prev, email: prev.email || det.email }));
          originalContactRef.current = { ...originalContactRef.current, email: originalContactRef.current?.email || det.email };
        }
      } catch {}
    }
  };

  const originalContactRef = useRef(null);

  const formatPhoneNumber = (raw) => {
    // Strip all non-digits
    const digits = raw.replace(/\D/g, '');
    // Remove leading 1 (country code) if 11 digits
    const core = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
    // Format as (XXX) XXX-XXXX when we have 10 digits
    if (core.length === 10) {
      return `(${core.slice(0, 3)}) ${core.slice(3, 6)}-${core.slice(6)}`;
    }
    return raw;
  };

  // Get raw digits for API payloads (no formatting characters)
  const getPhoneDigits = () => {
    const digits = (contactForm.phone || '').replace(/\D/g, '');
    return digits.length === 11 && digits.startsWith('1') ? digits : digits.length === 10 ? `1${digits}` : digits;
  };

  const updateField = (field, value) => {
    if (field === 'phone') {
      value = formatPhoneNumber(value);
    }
    setContactForm(prev => ({ ...prev, [field]: value }));
    setContactDirty(true);
  };

  // Update contact in CRM — only send fields that actually changed
  const handleUpdateContact = async () => {
    if (!selectedContact?.id) return;
    const missing = getMissingFields();
    if (missing.length > 0) { toast.error(`Please fill: ${missing.join(', ')}`); return; }
    setUpdatingContact(true);
    try {
      const orig = originalContactRef.current || {};
      const updatePayload = {};

      // Only update name if first/last actually changed
      const currentFirst = (contactForm.firstName || '').trim();
      const currentLast = (contactForm.lastName || '').trim();
      const origFirst = (orig.firstName || '').trim();
      const origLast = (orig.lastName || '').trim();
      if (currentFirst !== origFirst) updatePayload.first_name = currentFirst;
      if (currentLast !== origLast) updatePayload.last_name = currentLast;

      // Only update email/phone/dob if changed
      if (contactForm.email && contactForm.email !== (orig.email || '')) updatePayload.email = contactForm.email;
      if (contactForm.phone && contactForm.phone !== (orig.phone || '')) updatePayload.phone = contactForm.phone;
      if (contactForm.dateOfBirth && contactForm.dateOfBirth !== (orig.dateOfBirth || '')) updatePayload.date_of_birth = contactForm.dateOfBirth;

      // Update gender via custom field if changed
      if (contactForm.gender && contactForm.gender !== (orig.gender || '') && genderFieldId) {
        updatePayload.custom_fields = [{ id: genderFieldId, field_value: contactForm.gender }];
      }

      if (Object.keys(updatePayload).length === 0) {
        setContactDirty(false);
        return;
      }

      await ghlApi.updateContact(selectedContact.id, locationId, updatePayload);
      toast.success('Contact updated in CRM');
      setContactDirty(false);
      // Update the original reference (strip UI-only notes from baseline)
      const { notes: _n, ...baseline } = contactForm;
      originalContactRef.current = baseline;
    } catch { toast.error('Failed to update contact'); }
    finally { setUpdatingContact(false); }
  };

  // Save new contact to CRM (without booking)
  const handleSaveNewContact = async () => {
    const fName = (contactForm.firstName || '').trim();
    const lName = (contactForm.lastName || '').trim();
    if (!fName) { toast.error('First name is required'); return; }
    if (!lName) { toast.error('Last name is required'); return; }
    if (!contactForm.phone?.trim() && !contactForm.email?.trim()) { toast.error('Phone or Email is required'); return; }
    setSavingNewContact(true);
    try {
      const createPayload = {
        first_name: fName, last_name: lName,
        name: `${fName} ${lName}`.trim(),
        email: contactForm.email || undefined, phone: getPhoneDigits() || undefined,
        gender: contactForm.gender || undefined, date_of_birth: contactForm.dateOfBirth || undefined,
      };
      const resp = await ghlApi.createContact(locationId, createPayload);
      const newContact = resp.data?.contact || resp.data;
      if (!newContact?.id) throw new Error('No contact ID returned');
      toast.success('Contact saved to CRM');
      // Switch to editing mode with the new contact. Strip UI-only fields
      // (notes) from the diff baseline so handleUpdateContact doesn't see
      // them as a "change" later.
      setSelectedContact(newContact);
      setContactDirty(false);
      const { notes: _n, ...baseline } = contactForm;
      originalContactRef.current = baseline;
    } catch (e) {
      const rawDetail = e?.response?.data?.detail;
      let errMsg = '';
      let isDuplicate = false;
      if (rawDetail && typeof rawDetail === 'object') {
        errMsg = rawDetail.message || 'Failed to create contact';
        isDuplicate = rawDetail.error_type === 'duplicate_contact';
      } else {
        errMsg = rawDetail || e?.message || 'Failed to create contact';
        isDuplicate = errMsg.toLowerCase().includes('duplicate') || errMsg.toLowerCase().includes('already exists');
      }
      if (isDuplicate) {
        toast.error('Patient Already Exists', { description: 'Search for the existing patient instead.' });
        const q = contactForm.email || contactForm.phone || fullName(contactForm);
        if (q) { setTab('search'); setSearchQuery(q); setSelectedContact(null); doSearch(q); }
      } else {
        toast.error(errMsg);
      }
    } finally { setSavingNewContact(false); }
  };

  // Verify against Juvonno (on-demand only)
  const handleVerifyJuvonno = async () => {
    if (!juvonnoId || !locationId) return;
    // If already verified, ask to re-verify
    if (verifyStatus === 'verified' || verifyStatus === 'mismatch') {
      setVerifyStatus('confirm-reverify');
      return;
    }
    doVerify();
  };

  const doVerify = async () => {
    setVerifyStatus('loading');
    try {
      const resp = await ghlApi.verifyJuvonnoPatient(locationId, juvonnoId, {
        name: fullName(contactForm),
        email: contactForm.email,
        phone: getPhoneDigits(),
        dob: contactForm.dateOfBirth,
      });
      const result = resp.data;
      const isVerified = result.verified;
      setVerifyStatus(isVerified ? 'verified' : 'mismatch');
      setVerifyDetails(result);
      if (isVerified) {
        const now = new Date().toISOString();
        setVerifiedAt(now);
        // Persist verification in DB
        if (selectedContact?.id) {
          ghlApi.markJuvonnoVerified(locationId, selectedContact.id, juvonnoId).catch(() => {});
        }
        // Also store in localStorage as fallback
        try {
          const stored = JSON.parse(localStorage.getItem('juvonno_verifications') || '{}');
          stored[juvonnoId] = now;
          localStorage.setItem('juvonno_verifications', JSON.stringify(stored));
        } catch {}
      }
    } catch {
      setVerifyStatus('mismatch');
      setVerifyDetails({ verified: false, mismatches: [{ field: 'API', crm: '', juvonno: 'Could not reach Juvonno' }] });
    }
  };

  // Open Juvonno profile
  const openJuvonnoProfile = () => {
    if (!juvonnoId || !currentLocation?.juvonno_portal_url) return;
    crmNavigate.openJuvonno(currentLocation.juvonno_portal_url, juvonnoId);
  };

  const getMissingFields = () => {
    const m = [];
    if (!contactForm.firstName?.trim()) m.push('First Name');
    if (!contactForm.lastName?.trim()) m.push('Last Name');
    if (!contactForm.email?.trim()) m.push('Email');
    if (!contactForm.phone?.trim()) m.push('Phone');
    if (!contactForm.gender) m.push('Gender');
    if (!contactForm.dateOfBirth) m.push('Date of Birth');
    return m;
  };

  const validateFields = () => {
    const errors = [];
    const fName = (contactForm.firstName || '').trim();
    const lName = (contactForm.lastName || '').trim();
    if (!fName) errors.push('First name is required');
    else if (/^\(?[\d\s\-\+\)]+$/.test(fName)) errors.push('First name cannot be a phone number');
    if (!lName) errors.push('Last name is required');
    else if (/^\(?[\d\s\-\+\)]+$/.test(lName)) errors.push('Last name cannot be a phone number');

    if (!contactForm.email?.trim()) errors.push('Email is required');
    else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
      if (!emailRegex.test(contactForm.email.trim())) errors.push('Email format is invalid');
    }
    if (!contactForm.phone?.trim()) errors.push('Phone is required');
    else {
      const phone = (contactForm.phone || '').replace(/[\s\-\(\)\.]/g, '');
      const phoneDigits = phone.replace(/^\+/, '');
      if (!/^\+?1?\d{10}$/.test(phone) && !/^\d{10,11}$/.test(phoneDigits)) errors.push('Phone must be valid Canadian/US (10 digits)');
    }
    if (!contactForm.gender) errors.push('Gender is required');
    if (!contactForm.dateOfBirth) errors.push('Date of Birth is required');

    if (errors.length > 0) {
      toast.error('Please complete all required fields', { description: errors.join(' • ') });
      return false;
    }
    return true;
  };

  // Submit with audit log
  const handleSubmit = async () => {
    if (!validateFields()) return;

    let contactId = selectedContact?.id;
    const composedName = fullName(contactForm);

    // ── For existing patients with Juvonno ID: verify before booking ──
    if (contactId && juvonnoId && verifyStatus !== 'verified') {
      try {
        const checkResp = await ghlApi.isJuvonnoVerified(locationId, contactId);
        if (checkResp.data?.verified) {
          setVerifyStatus('verified');
          setVerifiedAt(checkResp.data.verified_at);
        } else {
          const vResp = await ghlApi.verifyJuvonnoPatient(locationId, juvonnoId, {
            name: composedName, email: contactForm.email, phone: getPhoneDigits(), dob: contactForm.dateOfBirth,
          });
          if (vResp.data?.verified) {
            setVerifyStatus('verified');
            setVerifiedAt(new Date().toISOString());
            await ghlApi.markJuvonnoVerified(locationId, contactId, juvonnoId);
          } else {
            setVerifyStatus('mismatch');
            setVerifyDetails(vResp.data);
            toast.error('Juvonno verification failed — please review mismatches before booking');
            return;
          }
        }
      } catch {
        // Juvonno unreachable — proceed with booking
      }
    }

    // ── Create new contact in CRM if needed ──
    if (!contactId) {
      setSubmitting(true);
      
      // Search Juvonno first to check if patient exists there
      let foundJuvonnoId = null;
      try {
        const searchResp = await ghlApi.searchJuvonnoPatients(locationId, {
          email: contactForm.email, phone: contactForm.phone, name: composedName,
        });
        const juvResults = searchResp.data?.patients || [];
        if (juvResults.length > 0) {
          const juvPatient = juvResults[0];
          foundJuvonnoId = String(juvPatient.id);
          const juvName = `${juvPatient.first_name || ''} ${juvPatient.last_name || ''}`.trim();
          // Prompt user to confirm before proceeding
          setJuvonnoId(foundJuvonnoId);
          setVerifyStatus('found_correct');
          setVerifyDetails({ verified: false, found_profile: { id: foundJuvonnoId, name: juvName, email: juvPatient.email, phone: juvPatient.phone, date_of_birth: juvPatient.date_of_birth, gender: juvPatient.gender } });
          setSubmitting(false);
          toast.success(`Found in Juvonno: ${juvName} (ID: ${foundJuvonnoId}). Confirm to save & proceed.`);
          return;
        }
      } catch { /* Juvonno search unavailable — proceed without */ }

      try {
        const fName = (contactForm.firstName || '').trim();
        const lName = (contactForm.lastName || '').trim();
        const resp = await ghlApi.createContact(locationId, {
          first_name: fName, last_name: lName,
          name: composedName,
          email: contactForm.email || undefined, phone: getPhoneDigits() || undefined,
          gender: contactForm.gender || undefined, date_of_birth: contactForm.dateOfBirth || undefined,
        });
        contactId = resp.data?.contact?.id;
        if (!contactId) throw new Error('No contact ID returned');
        // Auto-created during Create Appointment flow → mark as a new contact
        setContactSource('created');
        toast.success('Contact created in CRM');
      } catch (e) {
        const rawDetail = e?.response?.data?.detail;
        let errMsg = '';
        let isDuplicate = false;
        if (rawDetail && typeof rawDetail === 'object') {
          errMsg = rawDetail.message || 'Failed to create contact';
          isDuplicate = rawDetail.error_type === 'duplicate_contact';
        } else {
          errMsg = rawDetail || e?.message || 'Failed to create contact';
          isDuplicate = errMsg.toLowerCase().includes('duplicate') || errMsg.toLowerCase().includes('already exists');
        }
        if (isDuplicate) {
          setLastError('Patient already exists. Please select the existing patient below.');
          setErrorType('duplicate_contact');
          toast.error('Patient Already Exists', { description: 'Searching for existing patient...' });
          const q = contactForm.email || contactForm.phone || composedName;
          if (q) { setTab('search'); setSearchQuery(q); setSelectedContact(null); doSearch(q); }
        } else {
          setLastError(errMsg);
          toast.error(errMsg);
        }
        setSubmitting(false);
        return;
      }
    }

    if (!calendarId) { toast.error('Select a calendar'); return; }
    if (!date || !time) { toast.error('Date and time are required'); return; }

    if (selectedContact && contactDirty) {
      try { await handleUpdateContact(); } catch {}
    }

    setSubmitting(true);
    setLastError(null);
    let crmSuccess = false;
    let crmResponse = '';
    let crmAppointmentId = '';
    try {
      let startTime = time;
      if (time.includes('AM') || time.includes('PM')) {
        const m = time.match(/(\d+):(\d+)\s*(AM|PM)/i);
        if (m) {
          let h = parseInt(m[1]); const min = m[2]; const p = m[3].toUpperCase();
          if (p === 'PM' && h !== 12) h += 12; if (p === 'AM' && h === 12) h = 0;
          startTime = `${h.toString().padStart(2, '0')}:${min}`;
        }
      }
      const patientName = composedName;
      const aptTitle = patientName
        ? `${appointmentType || 'Appointment'} - ${patientName}`
        : appointmentType || 'Appointment';
      const resp = await onSubmit({
        calendarId, contactId, date, startTime, duration, appointmentType,
        title: aptTitle, notes: notes || '', contactName: patientName,
        ignoreFreeSlotValidation: showForceSave,
      });
      crmSuccess = true;
      crmResponse = 'Created successfully';
      crmAppointmentId = resp?.data?.id || resp?.data?.event?.id || '';
      setShowForceSave(false);
      setLastError(null);

      onOpenChange(false);
      // Trigger post-booking flow
      if (onBooked) {
        const durationMinutes = parseInt(duration) || 30;
        const startDateTime = new Date(`${date}T${startTime}`);
        const endDateTime = new Date(startDateTime.getTime() + durationMinutes * 60000);
        onBooked({
          contactId: contactId,
          appointmentId: crmAppointmentId,
          contactName: composedName,
          appointmentType: appointmentType,
          startTime: startDateTime.toISOString(),
          endTime: endDateTime.toISOString(),
          calendarName: calendars.find(c => c.id === calendarId)?.name || '',
        });
      }
    } catch (e) {
      // Parse error - backend returns { message, error_type } in detail
      const rawDetail = e?.response?.data?.detail;
      let errMsg = '';
      let errorType = 'unknown';
      if (rawDetail && typeof rawDetail === 'object') {
        errMsg = rawDetail.message || 'Failed to create appointment';
        errorType = rawDetail.error_type || 'unknown';
      } else {
        errMsg = rawDetail || e?.message || 'Failed to create appointment';
      }
      crmResponse = errMsg;

      const isSchedulingError = ['outside_hours', 'existing_conflict', 'scheduling_conflict', 'slot_unavailable', 'bad_request'].includes(errorType)
        || errMsg.includes('outside') || errMsg.includes('conflict') || errMsg.includes('no longer available') || errMsg.includes('not available');

      if (isSchedulingError && !showForceSave) {
        setShowForceSave(true);
        // Use the grid's knowledge: if the slot was within available hours, it's a conflict.
        // If it was outside hours (Force Book from dashed line), it's outside working hours.
        let resolvedType = errorType;
        if (resolvedType === 'slot_unavailable' || resolvedType === 'bad_request') {
          resolvedType = prefill?.outsideHours ? 'outside_hours' : 'existing_conflict';
        }
        setErrorType(resolvedType);
        setLastError(errMsg);
        const title = resolvedType === 'existing_conflict'
          ? 'Schedule Conflict'
          : 'Outside Working Hours';
        toast.error(title, {
          description: 'Click "Force Save" to book anyway.',
        });
      } else {
        // Force Save also failed, or non-scheduling error
        setLastError(errMsg);
        toast.error(errMsg);
      }
    } finally {
      // Save notes to contact.apt_notes custom field — always, even if booking failed
      if (notes?.trim() && contactId) {
        try {
          const aptNotesField = customFieldDefs.find(f => f.fieldKey === 'contact.apt_notes');
          if (aptNotesField) {
            await ghlApi.updateContact(contactId, locationId, {
              customFields: [{ id: aptNotesField.id, field_value: notes.trim() }]
            });
          } else {
            console.warn('apt_notes custom field not found in field definitions');
          }
        } catch (noteErr) {
          console.error('Failed to save apt_notes:', noteErr);
        }
      }

      // Write audit log
      const calName = calendars.find(c => c.id === calendarId)?.name || calendarId;
      try {
        await ghlApi.createAppointmentLog({
          user_name: user?.name || user?.email || 'Unknown',
          user_email: user?.email || '',
          contact_name: composedName,
          contact_email: contactForm.email,
          contact_phone: getPhoneDigits(),
          contact_id: contactId || '',
          juvonno_customer_id: juvonnoId || '',
          calendar_name: calName,
          calendar_id: calendarId,
          appointment_type: appointmentType,
          date, time, duration,
          crm_success: crmSuccess,
          crm_response: crmResponse,
          crm_appointment_id: crmAppointmentId,
          location_id: locationId,
          location_name: currentLocation?.name || '',
          force_booked: showForceSave,
          notes: notes || '',
          ghl_payload: {
            calendarId, contactId, date, startTime: time, duration,
            appointmentType, title: `${appointmentType || 'Appointment'} - ${composedName}`,
            notes: notes || '', ignoreFreeSlotValidation: showForceSave,
            // Patient details
            name: composedName,
            firstName: (contactForm.firstName || '').trim(),
            lastName: (contactForm.lastName || '').trim(),
            email: contactForm.email || '',
            phone: getPhoneDigits() || '',
            gender: contactForm.gender || '',
            dateOfBirth: contactForm.dateOfBirth || '',
            juvonnoId: juvonnoId || '',
            // Booking type
            bookingType: contactSource === 'search' ? 'existing_contact' : 'new_contact',
          },
        });
      } catch {}
      setSubmitting(false);
    }
  };

  const showContactForm = selectedContact || tab === 'create';

  return {
    // State
    tab, setTab, searchQuery, setSearchQuery, searchResults, setSearchResults, searching,
    selectedContact, setSelectedContact, contactSource, setContactSource, showContactForm,
    contactForm, setContactForm, contactDirty, updatingContact, juvonnoId, setJuvonnoId,
    verifyStatus, verifyDetails, verifiedAt, setVerifyStatus, setVerifyDetails, setVerifiedAt,
    calendarId, setCalendarId, date, setDate, time, setTime,
    appointmentType, setAppointmentType, duration, setDuration,
    submitting, notes, setNotes, genderFieldId, customFieldDefs,
    showForceSave, setShowForceSave, lastError, errorType,
    savingNewContact, dupMatches, dupSearching,
    currentLocation, user,
    // Handlers
    handleDialogOpenChange, handleSelectContact, handleUpdateContact,
    handleVerifyJuvonno, handleSubmit,
    handleSaveNewContact, attemptCloseContactForm,
    updateField, getPhoneDigits, doVerify, openJuvonnoProfile,
    // Data
    DURATION_OPTIONS: [
      { value: '15', label: '15 minutes' }, { value: '20', label: '20 minutes' },
      { value: '30', label: '30 minutes' }, { value: '40', label: '40 minutes' },
      { value: '45', label: '45 minutes' }, { value: '60', label: '1 hour' },
      { value: '90', label: '1.5 hours' }, { value: '120', label: '2 hours' },
      { value: '180', label: '3 hours' },
    ],
    TIME_SLOTS: generateTimeSlots(),
    fullName,
    EMPTY_FORM,
    JUVONNO_FIELD_KEY,
    calendars, appointmentTypes, locationId,
  };
}
